import React, { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import FooterMicroDisclaimer from "../components/FooterMicroDisclaimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadsDashboard = () => {
  const [analytics, setAnalytics] = useState({
    by_source: {},
    speed_to_lead_median_sec: 0,
    appt_rate: 0.0,
    answer_rate: 0.0,
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7d");

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/analytics/leads-summary?range=${timeRange}`,
      );
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatPercent = (decimal) => {
    return `${Math.round(decimal * 100)}%`;
  };

  const getTotalLeads = () => {
    return Object.values(analytics.by_source).reduce(
      (sum, count) => sum + count,
      0,
    );
  };

  const getTopSource = () => {
    const sources = Object.entries(analytics.by_source);
    if (sources.length === 0) return "None";
    return sources.reduce(
      (top, [source, count]) =>
        count > (analytics.by_source[top] || 0) ? source : top,
      sources[0][0],
    );
  };

  if (loading) {
    return (
      <div>
        <Navigation />
        <div className="container py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p>Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />

      <main className="py-8">
        <div className="container">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-4xl font-bold">Lead Analytics</h1>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="select"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </div>
            <p className="text-gray-600">
              Track your lead generation performance and conversion metrics.
            </p>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Leads */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-2 text-gray-600">
                Total Leads
              </h3>
              <div className="text-3xl font-bold text-primary">
                {getTotalLeads()}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Top source: {getTopSource()}
              </p>
            </div>

            {/* Speed to Lead */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-2 text-gray-600">
                Speed to Lead
              </h3>
              <div className="text-3xl font-bold text-primary">
                {formatDuration(analytics.speed_to_lead_median_sec)}
              </div>
              <p className="text-sm text-gray-500 mt-1">Median response time</p>
            </div>

            {/* Appointment Rate */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-2 text-gray-600">
                Appointment Rate
              </h3>
              <div className="text-3xl font-bold text-primary">
                {formatPercent(analytics.appt_rate)}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Leads converted to appointments
              </p>
            </div>

            {/* Answer Rate */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-2 text-gray-600">
                Answer Rate
              </h3>
              <div className="text-3xl font-bold text-primary">
                {formatPercent(analytics.answer_rate)}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Calls answered by prospects
              </p>
            </div>
          </div>

          {/* Leads by Source */}
          <div className="card mb-8">
            <h3 className="text-2xl font-bold mb-6">Leads by Source</h3>

            {Object.keys(analytics.by_source).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(analytics.by_source)
                  .sort(([, a], [, b]) => b - a)
                  .map(([source, count]) => {
                    const percentage =
                      getTotalLeads() > 0 ? (count / getTotalLeads()) * 100 : 0;
                    return (
                      <div
                        key={source}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-4 h-4 bg-orange-500 rounded"></div>
                          <span className="font-medium">{source}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-orange-500 h-2 rounded-full"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="font-bold w-12 text-right">
                            {count}
                          </span>
                          <span className="text-gray-500 w-12 text-right">
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📊</div>
                <h4 className="text-xl font-semibold mb-2">No leads yet</h4>
                <p className="text-gray-600 mb-6">
                  Start generating leads to see analytics here.
                </p>
                <a href="/#/get-quotes" className="btn-primary">
                  Test Quote Form
                </a>
              </div>
            )}
          </div>

          {/* Performance Tips */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                💡 Performance Tips
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>
                    Respond to leads within 5 minutes for best conversion rates
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>Call immediately, then follow up with email</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>Track all interactions in your CRM</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>Test different lead sources to optimize ROI</span>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                🎯 Lead Sources
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <strong>GBP:</strong> Google Business Profile
                </div>
                <div>
                  <strong>YELP:</strong> Yelp Business Listings
                </div>
                <div>
                  <strong>HOUZZ:</strong> Houzz Professional Network
                </div>
                <div>
                  <strong>FACEBOOK:</strong> Facebook Business Page
                </div>
                <div>
                  <strong>NEXTDOOR:</strong> Nextdoor Local Network
                </div>
                <div>
                  <strong>BING:</strong> Bing Places for Business
                </div>
                <div>
                  <strong>APPLE_MAPS:</strong> Apple Maps Connect
                </div>
                <div>
                  <strong>WEBSITE:</strong> Direct website visitors
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <FooterMicroDisclaimer />
    </div>
  );
};

export default LeadsDashboard;
